import { useState } from 'react';
import { Exam } from '@/lib/types';
import { apiClient } from '@/lib/api';

interface StartExamModalProps {
  exam: Exam;
  courseTitle?: string;
  onClose: () => void;
  onStarted: (examId: number) => void;
}

export default function StartExamModal({ exam, courseTitle, onClose, onStarted }: StartExamModalProps) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timed = !!exam.duration_minutes;
  const questionCount = exam.question_count ?? exam.questions?.length ?? 0;
  const passMark = Number(exam.pass_mark ?? 50);

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    const res = await apiClient.startExam(exam.id);
    setStarting(false);
    if (res.success) {
      onStarted(exam.id);
    } else {
      setError(res.error || 'Could not start the exam.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-exam-title"
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Before you begin</p>
        <h2 id="start-exam-title" className="mt-1 text-lg font-semibold text-slate-900">
          {exam.title}
        </h2>
        {courseTitle && <p className="mt-1 text-sm text-slate-500">{courseTitle}</p>}

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-xs text-slate-400">Questions</dt>
            <dd className="font-medium text-slate-800">{questionCount}</dd>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-xs text-slate-400">Pass mark</dt>
            <dd className="font-medium text-slate-800">{passMark}%</dd>
          </div>
        </dl>

        {timed ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            <p className="font-semibold">This exam is timed. You have {exam.duration_minutes} minutes.</p>
            <p className="mt-1 text-amber-800">
              The timer starts when you confirm and cannot be paused. Unanswered questions are submitted as-is when time runs out.
            </p>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
            No time limit. You can take as long as you need.
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={starting}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {starting ? 'Starting…' : 'Start exam'}
          </button>
        </div>
      </div>
    </div>
  );
}
